import React, { useState, useEffect } from "react";
import { categoryApi } from "../services/api";
import { useAuth } from "../context/AuthContext";
import {
  FiFolder,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiX,
  FiInfo,
} from "react-icons/fi";
import { toast } from "react-toastify";
import Modal from "../components/Modal";

const CategoriesPage = () => {
  const { isAdmin } = useAuth();
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Category Modal State (with Related Values)
  const [catModalOpen, setCatModalOpen] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [catName, setCatName] = useState("");
  const [catDesc, setCatDesc] = useState("");
  const [catType, setCatType] = useState("serialized"); // 'serialized' or 'bulk'
  const [catValues, setCatValues] = useState([]);
  const [newValueInput, setNewValueInput] = useState("");
  const [categoryFilterTab, setCategoryFilterTab] = useState("all"); // 'all', 'serialized', 'bulk'

  // Subcategory Modal State (inline add/edit single value)
  const [subModalOpen, setSubModalOpen] = useState(false);
  const [editingSub, setEditingSub] = useState(null);
  const [selectedCatId, setSelectedCatId] = useState("");
  const [subName, setSubName] = useState("");
  const [subDesc, setSubDesc] = useState("");
  const [subBottomLine, setSubBottomLine] = useState(5);

  const [submitting, setSubmitting] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [catsRes, subsRes] = await Promise.all([
        categoryApi.getCategories(),
        categoryApi.getSubcategories(),
      ]);
      setCategories(catsRes.data || []);
      setSubcategories(subsRes.data || []);
    } catch (err) {
      toast.error(err.message || "Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Category Handlers
  const openAddCategory = () => {
    setEditingCat(null);
    setCatName("");
    setCatDesc("");
    setCatType("serialized");
    setCatValues([]);
    setNewValueInput("");
    setCatModalOpen(true);
  };

  const openEditCategory = (c) => {
    setEditingCat(c);
    setCatName(c.name || "");
    setCatDesc(c.description || "");
    setCatType(c.category_type || (c.is_serialized === 0 ? "bulk" : "serialized"));
    // Extract related values either from c.values or subcategories
    const related = (c.values && c.values.length > 0)
      ? c.values.map((v) => v.name)
      : subcategories.filter((s) => s.category_id === c.id).map((s) => s.name);
    setCatValues(related.length > 0 ? related : [c.name]);
    setNewValueInput("");
    setCatModalOpen(true);
  };

  const handleAddValueToCategory = (e) => {
    e?.preventDefault();
    const val = newValueInput.trim();
    if (!val) return;
    if (catValues.some((v) => v.toLowerCase() === val.toLowerCase())) {
      toast.warning(`Value "${val}" is already added.`);
      return;
    }
    setCatValues([...catValues, val]);
    setNewValueInput("");
  };

  const handleRemoveValueFromCategory = (indexToRemove) => {
    setCatValues(catValues.filter((_, idx) => idx !== indexToRemove));
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    if (!catName.trim()) {
      toast.warning("Category name is required");
      return;
    }

    // Default to category name if no values were added
    const finalValues = catValues.length > 0 ? catValues : [catName.trim()];
    const isSerializedVal = catType === "serialized" ? 1 : 0;

    try {
      setSubmitting(true);
      if (editingCat) {
        await categoryApi.updateCategory(editingCat.id, {
          name: catName.trim(),
          description: catDesc,
          values: finalValues,
          is_serialized: isSerializedVal,
          category_type: catType,
        });
        toast.success("Category & tracking rules updated successfully");
      } else {
        await categoryApi.createCategory({
          name: catName.trim(),
          description: catDesc,
          values: finalValues,
          is_serialized: isSerializedVal,
          category_type: catType,
        });
        toast.success("Category & tracking rules created successfully");
      }
      setCatModalOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.message || "Failed to save category");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCategory = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}" and all associated values?`)) return;
    try {
      await categoryApi.deleteCategory(cat.id);
      toast.success("Category deleted");
      fetchData();
    } catch (err) {
      toast.error(err.message || "Cannot delete category with related inventory records");
    }
  };

  // Subcategory Handlers
  const openAddSubcategory = (presetCatId = null) => {
    setEditingSub(null);
    setSelectedCatId(presetCatId || (categories[0]?.id ? String(categories[0].id) : ""));
    setSubName("");
    setSubDesc("");
    setSubBottomLine(5);
    setSubModalOpen(true);
  };

  const openEditSubcategory = (s) => {
    setEditingSub(s);
    setSelectedCatId(String(s.category_id));
    setSubName(s.name || "");
    setSubDesc(s.description || "");
    setSubBottomLine(s.min_stock_alert !== undefined && s.min_stock_alert !== null ? s.min_stock_alert : 5);
    setSubModalOpen(true);
  };

  const handleSaveSubcategory = async (e) => {
    e.preventDefault();
    if (!selectedCatId || !subName.trim()) {
      toast.warning("Please choose a category and enter value name");
      return;
    }
    const limit = parseInt(subBottomLine);
    const finalAlert = !isNaN(limit) && limit >= 0 ? limit : 5;
    try {
      setSubmitting(true);
      if (editingSub) {
        await categoryApi.updateSubcategory(editingSub.id, {
          category_id: selectedCatId,
          name: subName.trim(),
          description: subDesc,
          min_stock_alert: finalAlert,
        });
        toast.success("Value & bottom line updated successfully");
      } else {
        await categoryApi.createSubcategory({
          category_id: selectedCatId,
          name: subName.trim(),
          description: subDesc,
          min_stock_alert: finalAlert,
        });
        toast.success("Value & bottom line added successfully");
      }
      setSubModalOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.message || "Failed to save value");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSubcategory = async (sub) => {
    if (!window.confirm(`Delete value "${sub.name}"?`)) return;
    try {
      await categoryApi.deleteSubcategory(sub.id);
      toast.success("Value deleted");
      fetchData();
    } catch (err) {
      toast.error(err.message || "Cannot delete value with existing inventory items");
    }
  };

  return (
    <div className="fade-in">
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 24,
        }}
      >
        <div>
          <h2 style={{ fontSize: "1.45rem", fontWeight: 800, color: "var(--navy)" }}>
            Categories & Related Values
          </h2>
          <p style={{ fontSize: "0.85rem", color: "var(--slate-500)", marginTop: 2 }}>
            Define categories (e.g. PC, Laptop, Printer, Mouse) and configure their associated values/components (e.g. Laptop → Laptop; PC → LED, Desktop).
          </p>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={openAddCategory}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              backgroundColor: "var(--primary)",
              color: "#fff",
              padding: "10px 16px",
              borderRadius: "var(--radius-md)",
              border: "none",
              fontWeight: 700,
              fontSize: "0.88rem",
              cursor: "pointer",
              boxShadow: "var(--shadow-sm)",
            }}
          >
            <FiPlus size={18} />
            Add Category
          </button>
        </div>
      </div>

      {/* Info Callout explaining the structure */}
      <div
        style={{
          backgroundColor: "#F0FDF4",
          border: "1px solid #BBF7D0",
          borderRadius: "var(--radius-md)",
          padding: "12px 18px",
          marginBottom: 20,
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
        }}
      >
        <FiInfo size={20} color="#16A34A" style={{ marginTop: 2, flexShrink: 0 }} />
        <div style={{ fontSize: "0.84rem", color: "#166534", lineHeight: 1.5 }}>
          <strong>Category Value Rules:</strong> Most categories have 1 value (e.g. <strong>Laptop → Laptop</strong>, <strong>Printer → Printer</strong>, <strong>Mouse → Mouse</strong>).
          Composite categories like <strong>PC</strong> have multiple values (e.g. <strong>PC → LED, Desktop</strong>). When creating inventory items, the system generates rows for each related value based on the requested quantity.
        </div>
      </div>

      {/* Filter Tabs & Summary */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 18,
          borderBottom: "1.5px solid var(--slate-200)",
          paddingBottom: 12,
        }}
      >
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => setCategoryFilterTab("all")}
            style={{
              padding: "7px 16px",
              borderRadius: 8,
              fontSize: "0.84rem",
              fontWeight: 700,
              cursor: "pointer",
              border: categoryFilterTab === "all" ? "1.5px solid var(--navy)" : "1px solid var(--slate-300)",
              backgroundColor: categoryFilterTab === "all" ? "var(--navy)" : "#fff",
              color: categoryFilterTab === "all" ? "#fff" : "var(--slate-600)",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span>All Categories</span>
            <span
              style={{
                fontSize: "0.72rem",
                padding: "1px 6px",
                borderRadius: 10,
                backgroundColor: categoryFilterTab === "all" ? "rgba(255,255,255,0.2)" : "var(--slate-100)",
              }}
            >
              {categories.length}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setCategoryFilterTab("serialized")}
            style={{
              padding: "7px 16px",
              borderRadius: 8,
              fontSize: "0.84rem",
              fontWeight: 700,
              cursor: "pointer",
              border: categoryFilterTab === "serialized" ? "1.5px solid #2563EB" : "1px solid var(--slate-300)",
              backgroundColor: categoryFilterTab === "serialized" ? "#2563EB" : "#fff",
              color: categoryFilterTab === "serialized" ? "#fff" : "#1E40AF",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span>Serialized Hardware</span>
            <span
              style={{
                fontSize: "0.72rem",
                padding: "1px 6px",
                borderRadius: 10,
                backgroundColor: categoryFilterTab === "serialized" ? "rgba(255,255,255,0.2)" : "#DBEAFE",
              }}
            >
              {categories.filter((c) => c.is_serialized === 1 || c.category_type === "serialized").length}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setCategoryFilterTab("bulk")}
            style={{
              padding: "7px 16px",
              borderRadius: 8,
              fontSize: "0.84rem",
              fontWeight: 700,
              cursor: "pointer",
              border: categoryFilterTab === "bulk" ? "1.5px solid #059669" : "1px solid var(--slate-300)",
              backgroundColor: categoryFilterTab === "bulk" ? "#059669" : "#fff",
              color: categoryFilterTab === "bulk" ? "#fff" : "#065F46",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span>Bulk Accessories & Licenses</span>
            <span
              style={{
                fontSize: "0.72rem",
                padding: "1px 6px",
                borderRadius: 10,
                backgroundColor: categoryFilterTab === "bulk" ? "rgba(255,255,255,0.2)" : "#D1FAE5",
              }}
            >
              {categories.filter((c) => c.is_serialized === 0 || c.category_type === "bulk").length}
            </span>
          </button>
        </div>

        <div style={{ fontSize: "0.78rem", color: "var(--slate-500)", fontStyle: "italic" }}>
          * Bulk items do not track serial numbers; they are tracked by count & prices during purchase.
        </div>
      </div>

      {/* Grid of Categories with their Defined Related Values */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: 20 }}>
        {categories
          .filter((cat) => {
            if (categoryFilterTab === "serialized") return cat.is_serialized === 1 || cat.category_type === "serialized";
            if (categoryFilterTab === "bulk") return cat.is_serialized === 0 || cat.category_type === "bulk";
            return true;
          })
          .map((cat) => {
          const subs = (cat.values && cat.values.length > 0)
            ? cat.values
            : subcategories.filter((s) => s.category_id === cat.id);

          const isComposite = subs.length > 1;
          const isBulk = cat.is_serialized === 0 || cat.category_type === "bulk";

          return (
            <div
              key={cat.id}
              style={{
                backgroundColor: "#fff",
                borderRadius: "var(--radius-lg)",
                border: isBulk ? "1.5px solid #A7F3D0" : (isComposite ? "1.5px solid #CBD5E1" : "1px solid var(--slate-200)"),
                boxShadow: "var(--shadow-sm)",
                padding: "20px 22px",
                display: "flex",
                flexDirection: "column",
                position: "relative",
              }}
            >
              {/* Category Header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      backgroundColor: isBulk ? "#ECFDF5" : (isComposite ? "#EEF2FF" : "var(--primary-light)"),
                      color: isBulk ? "#059669" : (isComposite ? "#4F46E5" : "var(--primary)"),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FiFolder size={20} />
                  </div>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <h3 style={{ fontSize: "1.1rem", fontWeight: 800, color: "var(--navy)", margin: 0 }}>{cat.name}</h3>
                      {isBulk ? (
                        <span
                          style={{
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            padding: "2px 8px",
                            borderRadius: 12,
                            backgroundColor: "#ECFDF5",
                            color: "#047857",
                            border: "1px solid #A7F3D0",
                          }}
                        >
                          Bulk (Count & Price)
                        </span>
                      ) : (
                        <span
                          style={{
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            padding: "2px 8px",
                            borderRadius: 12,
                            backgroundColor: "#EFF6FF",
                            color: "#1D4ED8",
                            border: "1px solid #BFDBFE",
                          }}
                        >
                          Serialized Asset
                        </span>
                      )}
                      {isComposite && (
                        <span
                          style={{
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            padding: "2px 8px",
                            borderRadius: 12,
                            backgroundColor: "#EEF2FF",
                            color: "#4F46E5",
                            border: "1px solid #C7D2FE",
                          }}
                        >
                          {subs.length} Specs
                        </span>
                      )}
                    </div>
                    {cat.description && (
                      <p style={{ fontSize: "0.78rem", color: "var(--slate-500)", marginTop: 2 }}>{cat.description}</p>
                    )}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 4 }}>
                  <button
                    onClick={() => openEditCategory(cat)}
                    title="Edit category & values"
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 6,
                      color: "var(--slate-500)",
                      borderRadius: 6,
                    }}
                  >
                    <FiEdit2 size={16} />
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => handleDeleteCategory(cat)}
                      title="Delete category"
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 6,
                        color: "var(--danger)",
                        borderRadius: 6,
                      }}
                    >
                      <FiTrash2 size={16} />
                    </button>
                  )}
                </div>
              </div>

              {/* Related Values List */}
              <div
                style={{
                  marginTop: 10,
                  paddingTop: 14,
                  borderTop: "1px solid var(--slate-100)",
                  flex: 1,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "var(--slate-400)", textTransform: "uppercase" }}>
                    Related Value(s) ({subs.length})
                  </span>
                  <button
                    onClick={() => openAddSubcategory(cat.id)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      color: "var(--primary)",
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <FiPlus size={13} /> Add Value
                  </button>
                </div>

                {subs.length === 0 ? (
                  <div
                    style={{
                      padding: "10px 12px",
                      borderRadius: 8,
                      backgroundColor: "var(--slate-50)",
                      border: "1px dashed var(--slate-300)",
                      fontSize: "0.8rem",
                      color: "var(--slate-400)",
                      fontStyle: "italic",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span>No values defined yet.</span>
                    <button
                      onClick={() => openEditCategory(cat)}
                      style={{
                        padding: "3px 8px",
                        fontSize: "0.72rem",
                        borderRadius: 4,
                        border: "1px solid var(--primary)",
                        color: "var(--primary)",
                        background: "#fff",
                        cursor: "pointer",
                        fontWeight: 600,
                      }}
                    >
                      Define Values
                    </button>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {subs.map((s) => (
                      <div
                        key={s.id}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "8px 12px",
                          borderRadius: "var(--radius-md)",
                          backgroundColor: isComposite ? "#F8FAFC" : "var(--slate-50)",
                          border: "1px solid var(--slate-200)",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              backgroundColor: isComposite ? "#4F46E5" : "var(--primary)",
                            }}
                          />
                          <span style={{ fontSize: "0.88rem", fontWeight: 700, color: "var(--slate-800)" }}>
                            {s.name}
                          </span>
                          <span
                            title="Stock bottom line limit for alerts"
                            style={{
                              fontSize: "0.70rem",
                              fontWeight: 700,
                              padding: "1px 7px",
                              borderRadius: 10,
                              backgroundColor: "#FEF3C7",
                              color: "#92400E",
                              border: "1px solid #FDE68A",
                            }}
                          >
                            Bottom Line: {s.min_stock_alert !== undefined && s.min_stock_alert !== null ? s.min_stock_alert : 5}
                          </span>
                        </div>
                        <div style={{ display: "flex", gap: 4 }}>
                          <button
                            onClick={() => openEditSubcategory(s)}
                            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--slate-400)", padding: 4 }}
                            title="Edit value"
                          >
                            <FiEdit2 size={13} />
                          </button>
                          {isAdmin && (
                            <button
                              onClick={() => handleDeleteSubcategory(s)}
                              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--danger)", padding: 4 }}
                              title="Delete value"
                            >
                              <FiTrash2 size={13} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add / Edit Category Modal with Related Values management */}
      <Modal isOpen={catModalOpen} onClose={() => setCatModalOpen(false)} maxWidth={480}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ fontSize: "1.18rem", fontWeight: 800, color: "var(--navy)" }}>
            {editingCat ? "Edit Category & Values" : "Add New Category"}
          </h3>
          <button onClick={() => setCatModalOpen(false)} style={{ background: "none", border: "none", cursor: "pointer" }}>
            <FiX size={20} color="var(--slate-400)" />
          </button>
        </div>

        <form onSubmit={handleSaveCategory}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
              Category Name *
            </label>
            <input
              type="text"
              required
              placeholder="e.g. Desktop, LED, NVME - GB, RAM - GB, Mouse, Keyboard"
              value={catName}
              onChange={(e) => {
                const val = e.target.value;
                setCatName(val);
                // If creating new and no values entered yet, auto-suggest
                if (!editingCat && catValues.length === 0 && val.trim().length > 1) {
                  const upper = val.trim().toUpperCase();
                  if (upper.includes("NVME")) {
                    setCatValues(["128", "256", "512", "1024", "2048"]);
                  } else if (upper.includes("RAM")) {
                    setCatValues(["8", "16", "32"]);
                  }
                }
              }}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid var(--slate-300)" }}
            />
          </div>

          {/* Tracking Type: Serialized vs Bulk */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 8 }}>
              Category Tracking Mode *
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div
                onClick={() => setCatType("serialized")}
                style={{
                  border: catType === "serialized" ? "2px solid #2563EB" : "1px solid var(--slate-300)",
                  backgroundColor: catType === "serialized" ? "#EFF6FF" : "#fff",
                  borderRadius: 8,
                  padding: "10px 12px",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, fontSize: "0.84rem", color: catType === "serialized" ? "#1D4ED8" : "var(--navy)" }}>
                  <input
                    type="radio"
                    name="catType"
                    checked={catType === "serialized"}
                    onChange={() => setCatType("serialized")}
                    style={{ margin: 0, cursor: "pointer" }}
                  />
                  <span>Serialized Asset</span>
                </div>
                <div style={{ fontSize: "0.73rem", color: "var(--slate-500)", marginTop: 4, lineHeight: 1.3 }}>
                  Desktop, LED, Laptop, Printer. Tracked with unique Serial#, Part#, SKU.
                </div>
              </div>

              <div
                onClick={() => setCatType("bulk")}
                style={{
                  border: catType === "bulk" ? "2px solid #059669" : "1px solid var(--slate-300)",
                  backgroundColor: catType === "bulk" ? "#ECFDF5" : "#fff",
                  borderRadius: 8,
                  padding: "10px 12px",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, fontSize: "0.84rem", color: catType === "bulk" ? "#047857" : "var(--navy)" }}>
                  <input
                    type="radio"
                    name="catType"
                    checked={catType === "bulk"}
                    onChange={() => setCatType("bulk")}
                    style={{ margin: 0, cursor: "pointer" }}
                  />
                  <span>Bulk / Accessory</span>
                </div>
                <div style={{ fontSize: "0.73rem", color: "var(--slate-500)", marginTop: 4, lineHeight: 1.3 }}>
                  RAM, NVME, Mouse, Bags, Software. Tracked by Count & Prices, no serial/SKU.
                </div>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
              Description
            </label>
            <textarea
              rows={2}
              placeholder="Optional notes about this category..."
              value={catDesc}
              onChange={(e) => setCatDesc(e.target.value)}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid var(--slate-300)" }}
            />
          </div>

          {/* Related Values Section */}
          <div
            style={{
              marginBottom: 20,
              backgroundColor: "var(--slate-50)",
              border: "1px solid var(--slate-200)",
              borderRadius: 8,
              padding: 14,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <label style={{ fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-800)" }}>
                Related Values against this Category *
              </label>
              <div style={{ display: "flex", gap: 6 }}>
                {catName.trim() && (
                  <button
                    type="button"
                    onClick={() => {
                      if (!catValues.includes(catName.trim())) {
                        setCatValues([...catValues, catName.trim()]);
                      }
                    }}
                    style={{
                      fontSize: "0.72rem",
                      fontWeight: 600,
                      color: "var(--primary)",
                      background: "#fff",
                      border: "1px solid var(--primary-light)",
                      borderRadius: 4,
                      padding: "2px 6px",
                      cursor: "pointer",
                    }}
                  >
                    + Same as Category ({catName.trim()})
                  </button>
                )}
                {catName.trim().toUpperCase().includes("NVME") && (
                  <button
                    type="button"
                    onClick={() => setCatValues(["128", "256", "512", "1024", "2048"])}
                    style={{
                      fontSize: "0.72rem",
                      fontWeight: 600,
                      color: "#4F46E5",
                      background: "#EEF2FF",
                      border: "1px solid #C7D2FE",
                      borderRadius: 4,
                      padding: "2px 6px",
                      cursor: "pointer",
                    }}
                  >
                    Preset: 128, 256, 512, 1024, 2048
                  </button>
                )}
                {catName.trim().toUpperCase().includes("RAM") && (
                  <button
                    type="button"
                    onClick={() => setCatValues(["8", "16", "32"])}
                    style={{
                      fontSize: "0.72rem",
                      fontWeight: 600,
                      color: "#4F46E5",
                      background: "#EEF2FF",
                      border: "1px solid #C7D2FE",
                      borderRadius: 4,
                      padding: "2px 6px",
                      cursor: "pointer",
                    }}
                  >
                    Preset: 8, 16, 32
                  </button>
                )}
              </div>
            </div>

            <p style={{ fontSize: "0.75rem", color: "var(--slate-500)", marginBottom: 10 }}>
              Define relevant specifications or sub-values (e.g. for NVME: 128, 256, 512; for RAM: 8, 16, 32; for single items, same as category).
            </p>

            {/* Chips of added values */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10, minHeight: 32 }}>
              {catValues.length === 0 ? (
                <span style={{ fontSize: "0.78rem", color: "var(--slate-400)", fontStyle: "italic", alignSelf: "center" }}>
                  No values defined yet. Type below and press "Add Value".
                </span>
              ) : (
                catValues.map((val, idx) => (
                  <span
                    key={idx}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      backgroundColor: "#fff",
                      border: "1.5px solid var(--primary)",
                      color: "var(--primary)",
                      fontWeight: 700,
                      fontSize: "0.82rem",
                      padding: "4px 10px",
                      borderRadius: 16,
                      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                    }}
                  >
                    {val}
                    <button
                      type="button"
                      onClick={() => handleRemoveValueFromCategory(idx)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "var(--slate-400)",
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                      }}
                      title="Remove value"
                    >
                      <FiX size={14} />
                    </button>
                  </span>
                ))
              )}
            </div>

            {/* Input to add a value */}
            <div style={{ display: "flex", gap: 8 }}>
              <input
                type="text"
                placeholder="Enter value name (e.g. LED, Desktop)..."
                value={newValueInput}
                onChange={(e) => setNewValueInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddValueToCategory();
                  }
                }}
                style={{
                  flex: 1,
                  padding: "8px 12px",
                  borderRadius: 6,
                  border: "1px solid var(--slate-300)",
                  fontSize: "0.82rem",
                }}
              />
              <button
                type="button"
                onClick={handleAddValueToCategory}
                style={{
                  padding: "8px 14px",
                  borderRadius: 6,
                  border: "none",
                  backgroundColor: "var(--primary)",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.8rem",
                  cursor: "pointer",
                }}
              >
                + Add Value
              </button>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button
              type="button"
              onClick={() => setCatModalOpen(false)}
              style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid var(--slate-300)", background: "#fff", cursor: "pointer" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: "var(--primary)", color: "#fff", fontWeight: 700, cursor: "pointer" }}
            >
              {submitting ? "Saving..." : "Save Category & Values"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Add / Edit Single Value Modal */}
      <Modal isOpen={subModalOpen} onClose={() => setSubModalOpen(false)} maxWidth={420}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ fontSize: "1.15rem", fontWeight: 700, color: "var(--navy)" }}>
            {editingSub ? "Edit Related Value" : "Add Related Value"}
          </h3>
          <button onClick={() => setSubModalOpen(false)} style={{ background: "none", border: "none", cursor: "pointer" }}>
            <FiX size={20} color="var(--slate-400)" />
          </button>
        </div>
        <form onSubmit={handleSaveSubcategory}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
              Parent Category *
            </label>
            <select
              required
              value={selectedCatId}
              onChange={(e) => setSelectedCatId(e.target.value)}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid var(--slate-300)", backgroundColor: "#fff" }}
            >
              <option value="">-- Choose Category --</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
              Value Name *
            </label>
            <input
              type="text"
              required
              placeholder="e.g. LED, Desktop, Laptop, Printer"
              value={subName}
              onChange={(e) => setSubName(e.target.value)}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid var(--slate-300)" }}
            />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
              Bottom Line (Low Stock Alert Limit) *
            </label>
            <input
              type="number"
              min="0"
              required
              placeholder="e.g. 5"
              value={subBottomLine}
              onChange={(e) => setSubBottomLine(e.target.value)}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid var(--slate-300)" }}
            />
            <span style={{ fontSize: "0.74rem", color: "var(--slate-500)", marginTop: 4, display: "block" }}>
              Notification alert will trigger when available items for this value reach or drop below this limit.
            </span>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
              Description
            </label>
            <textarea
              rows={2}
              placeholder="Optional notes..."
              value={subDesc}
              onChange={(e) => setSubDesc(e.target.value)}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid var(--slate-300)" }}
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button
              type="button"
              onClick={() => setSubModalOpen(false)}
              style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid var(--slate-300)", background: "#fff", cursor: "pointer" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: "var(--primary)", color: "#fff", fontWeight: 700, cursor: "pointer" }}
            >
              {submitting ? "Saving..." : "Save Value"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default CategoriesPage;
